import { ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface BaseProps {
    className?: string
}

interface TypographyProps extends BaseProps {
    children: ReactNode
}

export function H1({ children, className }: TypographyProps) {
    return (
        <h1
            className={cn(
                'scroll-m-20 text-4xl font-extrabold tracking-tight text-foreground lg:text-5xl',
                className,
            )}
        >
            {children}
        </h1>
    )
}

export function H2({ children, className }: TypographyProps) {
    return (
        <h2
            className={cn(
                'scroll-m-20 pb-2 text-3xl font-semibold tracking-tight text-foreground first:mt-0',
                className,
            )}
        >
            {children}
        </h2>
    )
}

export function H3({ children, className }: TypographyProps) {
    return (
        <h3
            className={cn(
                'scroll-m-20 text-2xl font-semibold tracking-tight text-foreground',
                className,
            )}
        >
            {children}
        </h3>
    )
}

export function H4({ children, className }: TypographyProps) {
    return (
        <h4
            className={cn(
                'scroll-m-20 text-xl font-semibold tracking-tight text-foreground',
                className,
            )}
        >
            {children}
        </h4>
    )
}

export function Paragraph({ children, className }: TypographyProps) {
    return (
        <p
            className={cn(
                'leading-7 text-foreground [&:not(:first-child)]:mt-6',
                className,
            )}
        >
            {children}
        </p>
    )
}

export function Blockquote({ children, className }: TypographyProps) {
    return (
        <blockquote
            className={cn(
                'mt-6 border-l-2 border-border pl-6 italic text-foreground',
                className,
            )}
        >
            {children}
        </blockquote>
    )
}

export function List({ children, className }: TypographyProps) {
    return (
        <ul
            className={cn(
                'my-6 ml-6 list-disc text-foreground [&>li]:mt-2',
                className,
            )}
        >
            {children}
        </ul>
    )
}

export function InlineCode({ children, className }: TypographyProps) {
    return (
        <code
            className={cn(
                'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold text-foreground',
                className,
            )}
        >
            {children}
        </code>
    )
}

export function Lead({ children, className }: TypographyProps) {
    return (
        <p className={cn('text-xl text-muted-foreground', className)}>
            {children}
        </p>
    )
}

export function Large({ children, className }: TypographyProps) {
    return (
        <div className={cn('text-lg font-semibold text-foreground', className)}>
            {children}
        </div>
    )
}

export function Medium({ children, className }: TypographyProps) {
    return (
        <div className={cn('text-md font-normal text-foreground', className)}>
            {children}
        </div>
    )
}

export function Small({ children, className }: TypographyProps) {
    return (
        <small
            className={cn(
                'text-sm font-medium leading-none text-foreground',
                className,
            )}
        >
            {children}
        </small>
    )
}

export function XSmall({ children, className }: TypographyProps) {
    return (
        <small
            className={cn('text-xs leading-none text-foreground', className)}
        >
            {children}
        </small>
    )
}

export function Muted({ children, className }: TypographyProps) {
    return (
        <p className={cn('text-sm text-muted-foreground', className)}>
            {children}
        </p>
    )
}

interface TableProps extends BaseProps {
    children?: ReactNode
    headers?: string[]
    rows?: string[][]
}

export function Table({ children, className, headers, rows }: TableProps) {
    return (
        <div className={cn('my-6 w-full overflow-y-auto', className)}>
            <table className="w-full text-foreground">
                {headers && (
                    <thead>
                        <tr className="m-0 border-t p-0 border-border even:bg-muted">
                            {headers.map((header, index) => (
                                <th
                                    key={index}
                                    className="border px-4 py-2 text-left font-bold border-border [&[align=center]]:text-center [&[align=right]]:text-right"
                                >
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                )}
                {rows && (
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr
                                key={rowIndex}
                                className="m-0 border-t p-0 border-border even:bg-muted"
                            >
                                {row.map((cell, cellIndex) => (
                                    <td
                                        key={cellIndex}
                                        className="border px-4 py-2 text-left border-border [&[align=center]]:text-center [&[align=right]]:text-right"
                                    >
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                )}
                {children && !rows && <tbody>{children}</tbody>}
            </table>
        </div>
    )
}

export function TypographyDemo() {
    return (
        <div className="text-foreground">
            <H1>The Joke Tax Chronicles</H1>
            <Paragraph>
                Once upon a time, in a far-off land, there was a very lazy king
                who spent all day lounging on his throne. One day, his advisors
                came to him with a problem: the kingdom was running out of
                money.
            </Paragraph>
            <H2>The King&apos;s Plan</H2>
            <Paragraph>
                The king thought long and hard, and finally came up with{' '}
                <a
                    href="#"
                    className="font-medium underline underline-offset-4 text-primary"
                >
                    a brilliant plan
                </a>
                : he would tax the jokes in the kingdom.
            </Paragraph>
            <Blockquote>
                &quot;After all,&quot; he said, &quot;everyone enjoys a good
                joke, so it&apos;s only fair that they should pay for the
                privilege.&quot;
            </Blockquote>
            <H3>The Joke Tax</H3>
            <Paragraph>
                The king&apos;s subjects were not amused. They grumbled and
                complained, but the king was firm:
            </Paragraph>
            <List>
                <li>1st level of puns: 5 gold coins</li>
                <li>2nd level of jokes: 10 gold coins</li>
                <li>3rd level of one-liners : 20 gold coins</li>
            </List>
        </div>
    )
}
